import { Box, Grid, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Eyebrow, Section, SectionHeader } from "../ui/primitives";
import { colors, company } from "../ui/tokens";
import PageHero from "../widgets/PageHero";
import CtaBand from "../widgets/CtaBand";

function Row({ label, children }) {
    return (
        <Box sx={{ py: 2, borderTop: `1px solid ${colors.line}` }}>
            <Grid container spacing={1}>
                <Grid item xs={12} sm={4}>
                    <Typography sx={{ color: colors.muted, fontSize: "0.92rem" }}>{label}</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                    <Box sx={{ color: colors.ink, fontWeight: 600, fontSize: "0.98rem", fontVariantNumeric: "tabular-nums" }}>
                        {children}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export function AboutUs() {
    const { t } = useTranslation("AboutUsPage");

    return (
        <>
            <Helmet>
                <title>{t("meta.title")}</title>
                <meta name="description" content={t("meta.description")} data-rh="true" />
                <link rel="canonical" href="https://oxide-digital.com/aboutus" />
            </Helmet>
            <PageHero eyebrow={t("hero.eyebrow")} title={t("hero.title")} lead={t("hero.lead")} />

            <Section tone="paper" py={{ xs: 9, md: 12 }}>
                <Grid container spacing={{ xs: 5, md: 10 }}>
                    <Grid item xs={12} md={5}>
                        <Typography variant="h2" component="h2">
                            {t("story.title")}
                        </Typography>
                        <Box
                            sx={{
                                mt: { xs: 3, md: 5 },
                                borderRadius: "14px",
                                backgroundColor: colors.navy,
                                color: colors.onDark,
                                p: { xs: 3, md: 4 },
                            }}
                        >
                            <Box component="img" src="/assets/oxide/oxideicondark.svg" alt="" sx={{ height: 44, width: "auto", mb: { xs: 3, md: 6 } }} />
                            <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, letterSpacing: "-0.01em" }}>{t("location.title")}</Typography>
                            <Typography sx={{ color: colors.onDarkMuted }}>{t("location.body")}</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        {["p1", "p2", "p3"].map((k) => (
                            <Typography
                                key={k}
                                sx={{ fontSize: { xs: "1.05rem", md: "1.18rem" }, lineHeight: 1.7, color: colors.ink, mb: 3 }}
                            >
                                {t(`story.${k}`)}
                            </Typography>
                        ))}
                    </Grid>
                </Grid>
            </Section>

            <Section tone="warm" py={{ xs: 9, md: 12 }}>
                <SectionHeader title={t("values.title")} />
                <Grid container spacing={{ xs: 4, md: 4 }}>
                    {["v1", "v2", "v3", "v4"].map((k) => (
                        <Grid item xs={12} sm={6} md={3} key={k}>
                            <Box sx={{ borderTop: `1px solid ${colors.ink}`, pt: 2.5 }}>
                                <Typography variant="h4" component="h3" sx={{ mb: 1.25 }}>
                                    {t(`values.${k}.title`)}
                                </Typography>
                                <Typography sx={{ color: colors.body, fontSize: "0.97rem" }}>{t(`values.${k}.body`)}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
                <Box sx={{ mt: { xs: 7, md: 10 } }}>
                    <Typography variant="h4" component="h3" sx={{ mb: 2.5 }}>
                        {t("sectors.title")}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
                        {t("sectors.list", { returnObjects: true }).map((s) => (
                            <Box
                                key={s}
                                sx={{
                                    px: 2,
                                    py: 1,
                                    borderRadius: "999px",
                                    border: `1px solid ${colors.line}`,
                                    backgroundColor: colors.paper,
                                    fontSize: "0.92rem",
                                    fontWeight: 500,
                                    color: colors.ink,
                                }}
                            >
                                {s}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Section>

            <Section tone="paper" py={{ xs: 9, md: 12 }} id="company">
                <Grid container spacing={{ xs: 4, md: 10 }}>
                    <Grid item xs={12} md={5}>
                        <Eyebrow>{t("company.eyebrow")}</Eyebrow>
                        <Typography variant="h2" component="h2">
                            {t("company.title")}
                        </Typography>
                        <Typography sx={{ mt: 2.5, color: colors.body, fontSize: "1.05rem" }}>{t("company.lead")}</Typography>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <Row label={t("company.legalName")}>{company.legalName}</Row>
                        <Row label={t("company.address")}>
                            {company.street}, {company.district}
                            <br />
                            {company.city}, {company.country}
                        </Row>
                        <Row label={t("company.rccm")}>{company.rccm}</Row>
                        <Row label={t("company.idnat")}>{company.idNat}</Row>
                        <Row label={t("company.tax")}>{company.taxNo}</Row>
                        <Row label={t("company.duns")}>{company.duns}</Row>
                        <Row label={t("company.phone")}>
                            <Box component="a" href={company.phoneHref} sx={{ color: "inherit" }}>
                                {company.phone}
                            </Box>
                        </Row>
                        <Row label={t("company.email")}>
                            <Box component="a" href={`mailto:${company.email}`} sx={{ color: "inherit" }}>
                                {company.email}
                            </Box>
                        </Row>
                        <Row label={t("company.website")}>{company.website}</Row>
                    </Grid>
                </Grid>
            </Section>

            <CtaBand />
        </>
    );
}
